// frontend/src/pages/admin/reports/OrdersReport.js
import React, { useState, useEffect } from 'react';
import axios from '../../../utils/axios';
import { 
  FiShoppingCart, 
  FiDollarSign, 
  FiTrendingUp, 
  FiAlertCircle,
  FiFilter,
  FiDownload,
  FiEye
} from 'react-icons/fi';
import { Link } from 'react-router-dom';
import DateRangePicker from '../../../components/DateRangePicker';

const OrdersReport = () => {
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({
    total_orders: 0,
    total_sales: 0,
    average_order_value: 0,
    unpaid_amount: 0
  });
  const [orders, setOrders] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });

  // Filters
  const [filters, setFilters] = useState({
    start_date: '',
    end_date: '',
    status: '',
    payment_method: '',
    payment_status: ''
  });

  useEffect(() => {
    fetchAllData();
  }, [filters, pagination.page]);

  const fetchAllData = async () => {
    await Promise.all([
      fetchStats(),
      fetchOrders()
    ]);
  };

  const handleDateChange = (start, end) => {
    setFilters(prev => ({
      ...prev,
      start_date: start,
      end_date: end
    }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const fetchStats = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);

      const response = await axios.get(`/api/reports/orders/stats?${params}`);
      
      if (response.data.success) {
        setStats(response.data.data.overall);
      }
    } catch (error) {
      console.error('Fetch stats error:', error);
    }
  };

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      if (filters.status) params.append('status', filters.status);
      if (filters.payment_method) params.append('payment_method', filters.payment_method);
      if (filters.payment_status) params.append('payment_status', filters.payment_status);
      params.append('page', pagination.page);
      params.append('limit', pagination.limit);

      const response = await axios.get(`/api/reports/orders?${params}`);
      
      if (response.data.success) {
        setOrders(response.data.data.orders);
        setPagination(prev => ({
          ...prev,
          ...response.data.data.pagination
        }));
      }
    } catch (error) {
      console.error('Fetch orders error:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('mn-MN', {
      style: 'decimal',
      minimumFractionDigits: 0
    }).format(amount) + '₮';
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { label: 'Хүлээгдэж буй', class: 'bg-yellow-100 text-yellow-800' },
      accepted: { label: 'Баталгаажсан', class: 'bg-blue-100 text-blue-800' },
      prepared: { label: 'Бэлтгэгдсэн', class: 'bg-purple-100 text-purple-800' },
      dispatched: { label: 'Илгээгдсэн', class: 'bg-indigo-100 text-indigo-800' },
      delivered: { label: 'Хүргэгдсэн', class: 'bg-green-100 text-green-800' },
      cancelled: { label: 'Цуцлагдсан', class: 'bg-red-100 text-red-800' }
    };
    const config = statusConfig[status] || { label: status, class: 'bg-gray-100 text-gray-800' };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.class}`}>
        {config.label}
      </span>
    );
  };

  const getPaymentStatusBadge = (status) => {
    const statusConfig = {
      unpaid: { label: 'Төлөөгүй', class: 'bg-red-100 text-red-800' },
      partial: { label: 'Хэсэгчлэн', class: 'bg-yellow-100 text-yellow-800' },
      paid: { label: 'Төлсөн', class: 'bg-green-100 text-green-800' }
    };
    const config = statusConfig[status] || { label: status, class: 'bg-gray-100 text-gray-800' };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.class}`}>
        {config.label}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Date Range Picker */}
      <DateRangePicker onDateChange={handleDateChange} />

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Total Orders */}
        <div className="card bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-blue-600 font-medium mb-1">Нийт захиалга</p>
              <p className="text-3xl font-bold text-blue-900">
                {stats.total_orders || 0}
              </p>
            </div>
            <div className="p-3 bg-blue-600 rounded-lg">
              <FiShoppingCart className="text-white" size={24} />
            </div>
          </div>
        </div>

        {/* Total Sales */}
        <div className="card bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-green-600 font-medium mb-1">Нийт борлуулалт</p>
              <p className="text-3xl font-bold text-green-900">
                {formatCurrency(stats.total_sales || 0)}
              </p>
            </div>
            <div className="p-3 bg-green-600 rounded-lg">
              <FiDollarSign className="text-white" size={24} />
            </div>
          </div>
        </div>

        {/* Average Order Value */}
        <div className="card bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-purple-600 font-medium mb-1">Дундаж захиалга</p>
              <p className="text-3xl font-bold text-purple-900">
                {formatCurrency(stats.average_order_value || 0)}
              </p>
            </div>
            <div className="p-3 bg-purple-600 rounded-lg">
              <FiTrendingUp className="text-white" size={24} />
            </div>
          </div>
        </div>

        {/* Unpaid Amount */}
        <div className="card bg-gradient-to-br from-red-50 to-red-100 border-red-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-red-600 font-medium mb-1">Төлөөгүй дүн</p>
              <p className="text-3xl font-bold text-red-900">
                {formatCurrency(stats.unpaid_amount || 0)}
              </p>
            </div>
            <div className="p-3 bg-red-600 rounded-lg">
              <FiAlertCircle className="text-white" size={24} />
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="flex items-center gap-2 mb-4">
          <FiFilter className="text-gray-600" />
          <h3 className="font-semibold text-gray-900">Шүүлтүүр</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Status Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Захиалгын төлөв
            </label>
            <select
              value={filters.status}
              onChange={(e) => {
                setFilters(prev => ({ ...prev, status: e.target.value }));
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
              className="input"
            >
              <option value="">Бүгд</option>
              <option value="pending">Хүлээгдэж буй</option>
              <option value="accepted">Баталгаажсан</option>
              <option value="prepared">Бэлтгэгдсэн</option>
              <option value="dispatched">Илгээгдсэн</option>
              <option value="delivered">Хүргэгдсэн</option>
              <option value="cancelled">Цуцлагдсан</option>
            </select>
          </div>

          {/* Payment Method Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Төлбөрийн хэлбэр
            </label>
            <select
              value={filters.payment_method}
              onChange={(e) => {
                setFilters(prev => ({ ...prev, payment_method: e.target.value }));
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
              className="input"
            >
              <option value="">Бүгд</option>
              <option value="cash">Бэлэн</option>
              <option value="card">Карт</option>
              <option value="transfer">Шилжүүлэг</option>
              <option value="credit">Зээл</option>
            </select>
          </div>

          {/* Payment Status Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Төлбөрийн төлөв
            </label>
            <select
              value={filters.payment_status}
              onChange={(e) => {
                setFilters(prev => ({ ...prev, payment_status: e.target.value }));
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
              className="input"
            >
              <option value="">Бүгд</option>
              <option value="unpaid">Төлөөгүй</option>
              <option value="partial">Хэсэгчлэн</option>
              <option value="paid">Төлсөн</option>
            </select>
          </div>
        </div>
      </div>

      {/* Orders Table */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-gray-900">
            Захиалгын жагсаалт ({pagination.total})
          </h3>
          <button className="btn btn-secondary flex items-center gap-2">
            <FiDownload size={16} />
            Экспорт
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : orders.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            Захиалга олдсонгүй
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Дугаар
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Харилцагч
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Дүн
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Төлбөрийн хэлбэр
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Төлбөрийн төлөв
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Захиалгын төлөв
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Огноо
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Үйлдэл
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {orders.map((order) => (
                    <tr key={order.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        #{order.order_number}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{order.customer_name}</div>
                        <div className="text-sm text-gray-500">{order.customer_phone}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {formatCurrency(order.total_amount)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {order.payment_method === 'cash' && 'Бэлэн'}
                        {order.payment_method === 'card' && 'Карт'}
                        {order.payment_method === 'transfer' && 'Шилжүүлэг'}
                        {order.payment_method === 'credit' && 'Зээл'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getPaymentStatusBadge(order.payment_status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(order.status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(order.created_at).toLocaleDateString('mn-MN')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <Link
                          to={`/admin/orders/${order.id}`}
                          className="text-blue-600 hover:text-blue-800 flex items-center gap-1"
                        >
                          <FiEye size={16} />
                          Харах
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.pages > 1 && (
              <div className="flex items-center justify-between mt-4">
                <div className="text-sm text-gray-700">
                  Нийт {pagination.total} захиалга, {pagination.page}/{pagination.pages} хуудас
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                    disabled={pagination.page === 1}
                    className="btn btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Өмнөх
                  </button>
                  <button
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                    disabled={pagination.page === pagination.pages}
                    className="btn btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Дараах
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default OrdersReport;